import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ActivityEntrySchema,
  MyTaskSchema,
  ProjectWorkspaceSchema,
  TaskSummarySchema,
  WorkProjectSchema,
  type TaskSummaryDto,
  type WorkProjectDto,
} from '@taqon/contracts';
import { z } from 'zod';
import { getData } from '../../../shared/api/client';
import { hasCapability, useSession } from '../../../shared/session/session';

/**
 * Project work management (CLR-66): the API paths, query keys and reads shared by the project's
 * work pages, My tasks and the dashboard. The API decides who sees and changes what; the pages
 * only hide what the workspace says the caller cannot do.
 */
export const WORK = '/api/v1/project-work';

export const workKeys = {
  all: ['project-work'] as const,
  projects: ['project-work', 'projects'] as const,
  workspace: (projectId: string) => ['project-work', 'workspace', projectId] as const,
  tasks: (projectId: string) => ['project-work', 'tasks', projectId] as const,
  task: (taskId: string) => ['project-work', 'task', taskId] as const,
  comments: (taskId: string) => ['project-work', 'comments', taskId] as const,
  activity: (projectId: string, taskId?: string) =>
    ['project-work', 'activity', projectId, taskId ?? ''] as const,
  mine: ['project-work', 'my-tasks'] as const,
};

export type WorkProject = WorkProjectDto;

export function useCanUseWork(): boolean {
  const session = useSession();
  return hasCapability(session.data, 'projects.task:view');
}

export function useWorkProjects() {
  return useQuery({
    queryKey: workKeys.projects,
    queryFn: () => getData(`${WORK}/projects`, z.array(WorkProjectSchema)),
  });
}

export function useWorkspace(projectId: string) {
  return useQuery({
    queryKey: workKeys.workspace(projectId),
    queryFn: () => getData(`${WORK}/projects/${projectId}/workspace`, ProjectWorkspaceSchema),
  });
}

export function useTasks(projectId: string) {
  return useQuery({
    queryKey: workKeys.tasks(projectId),
    queryFn: () =>
      getData(
        `${WORK}/projects/${projectId}/tasks?includeArchived=false`,
        z.array(TaskSummarySchema),
      ),
  });
}

export function useActivity(projectId: string, taskId?: string) {
  const query = taskId === undefined ? '' : `?taskId=${taskId}`;
  return useQuery({
    queryKey: workKeys.activity(projectId, taskId),
    queryFn: () =>
      getData(`${WORK}/projects/${projectId}/activity${query}`, z.array(ActivityEntrySchema)),
  });
}

export function useMyTasks(enabled = true) {
  return useQuery({
    queryKey: workKeys.mine,
    enabled,
    queryFn: () => getData(`${WORK}/my-tasks`, z.array(MyTaskSchema)),
  });
}

/**
 * A write to the project's work: on success the workspace, its tasks and their activity are read
 * again, and so are the caller's own tasks, since any of them may have changed.
 */
export function useWorkMutation<T, V = void>(
  projectId: string,
  mutationFn: (variables: V) => Promise<T>,
  onDone?: (result: T) => void,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: workKeys.workspace(projectId) });
      await queryClient.invalidateQueries({ queryKey: workKeys.tasks(projectId) });
      await queryClient.invalidateQueries({ queryKey: ['project-work', 'task'] });
      await queryClient.invalidateQueries({ queryKey: ['project-work', 'activity', projectId] });
      await queryClient.invalidateQueries({ queryKey: workKeys.mine });
      onDone?.(result);
    },
  });
}

export function useCsrf(): string {
  return useSession().data?.csrfToken ?? '';
}

export const PRIORITY_LABELS = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
} as const;

/** Priority as a tinted chip: colour as an accent only, the word always shown. */
export const PRIORITY_COLOURS = {
  low: 'default',
  medium: 'info',
  high: 'warning',
  urgent: 'error',
} as const;

export const CATEGORY_LABELS = {
  todo: 'To do',
  in_progress: 'In progress',
  review: 'Review',
  done: 'Done',
} as const;

export const ROLE_LABELS = { manager: 'Manager', member: 'Member', viewer: 'Viewer' } as const;

/** Top-level tasks of a column, in their order on the board. */
export function columnTasks(tasks: readonly TaskSummaryDto[], columnId: string): TaskSummaryDto[] {
  return tasks
    .filter((t) => t.columnId === columnId && t.parentTaskId === null)
    .sort((a, b) => a.position - b.position);
}

/** Minutes as "2 h 30 min", "45 min" or "3 h". */
export function formatEstimate(minutes: number | null): string | null {
  if (minutes === null) return null;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${String(rest)} min`;
  return rest === 0 ? `${String(hours)} h` : `${String(hours)} h ${String(rest)} min`;
}
